import { registerTour } from "../tourRegistry";
import { getDragDropAnimationHTML } from "../animations/DragDropAnimation";

function getSteps() {
  const totalSteps = 8;
  const stepCounter = (n) => `Step ${n} of ${totalSteps}`;

  return [
    {
      id: 'coa-overview',
      title: `Chart of Accounts <span class="t3-shepherd-step-count">${stepCounter(1)}</span>`,
      text: 'This page organizes all your financial accounts by type — Assets, Liabilities, Income, and Expenses. Each section can be expanded or collapsed.',
      attachTo: { element: '[data-tour="coa-page"]', on: 'bottom' },
      buttons: [
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },
    {
      id: 'coa-type-section',
      title: `Expand & Collapse <span class="t3-shepherd-step-count">${stepCounter(2)}</span>`,
      text: 'Click any section header to expand or collapse it. <strong>Collapsed sections hide the detail rows and show just the section title</strong> — useful for focusing on one account type at a time.',
      attachTo: { element: '[data-tour="coa-type-section"]', on: 'top' },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },
    {
      id: 'coa-rearrange',
      title: `Rearrange Accounts <span class="t3-shepherd-step-count">${stepCounter(3)}</span>`,
      text: `Click the drag handle on any account or group row to enter rearrange mode. Drag and drop to reorder — the order you set here is reflected in all your reports.${getDragDropAnimationHTML()}`,
      attachTo: { element: '[data-tour="coa-account-drag-handle"]', on: 'right' },
      beforeShowPromise() {
        return new Promise((resolve) => {
          // Ensure the first type section (Assets) is expanded so account rows are visible
          const typeSection = document.querySelector('[data-tour="coa-type-section"]');
          if (typeSection) {
            const expandMoreIcon = typeSection.querySelector('[data-testid="ExpandMoreIcon"]');
            if (expandMoreIcon) {
              const headerEl = typeSection.firstElementChild;
              if (headerEl) headerEl.click();
            }
          }
          setTimeout(resolve, 400);
        });
      },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },
    {
      id: 'coa-add-account-btn',
      title: `Add New Account <span class="t3-shepherd-step-count">${stepCounter(4)}</span>`,
      text: 'Click here to add a new account. The form opens inline so you can fill in all the details without leaving the page.',
      attachTo: { element: '[data-tour="coa-add-account"]', on: 'bottom' },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Open Form',
          classes: 't3-shepherd-next-btn',
          action() {
            const addBtn = document.querySelector('[data-tour="coa-add-account"]');
            if (addBtn) addBtn.click();
            setTimeout(() => this.next(), 400);
          },
        },
      ],
    },
    {
      id: 'coa-sub-account',
      title: `Sub Accounts <span class="t3-shepherd-step-count">${stepCounter(5)}</span>`,
      text: 'Toggle this on to make the new account a <strong>sub account</strong>. Sub accounts nest under a parent account — for example, a "Petty Cash" sub account under "Cash on Hand". In reports, sub account balances roll up into their parent\'s total.',
      attachTo: { element: '[data-tour="coa-sub-account-toggle"]', on: 'bottom' },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() {
            // Close the top-level form before moving on
            const addBtn = document.querySelector('[data-tour="coa-add-account"]');
            if (addBtn) addBtn.click();
            setTimeout(() => this.next(), 400);
          },
        },
      ],
    },
    {
      id: 'coa-group-add',
      title: `Add to a Group <span class="t3-shepherd-step-count">${stepCounter(6)}</span>`,
      text: 'You can also add accounts directly to a specific group using the <strong>+</strong> icon on the group header. This pre-fills the account type and group for you. You can also rename groups by clicking the pencil icon, and toggle individual accounts on or off.',
      attachTo: { element: '[data-tour="coa-group-add-btn"]', on: 'bottom' },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Open Form',
          classes: 't3-shepherd-next-btn',
          action() {
            // Click the group-level "+" to open an inline form with Assets pre-filled
            const groupAddBtn = document.querySelector('[data-tour="coa-group-add-btn"]');
            if (groupAddBtn) groupAddBtn.dispatchEvent(new MouseEvent('click', { bubbles: true }));
            setTimeout(() => this.next(), 400);
          },
        },
      ],
    },
    {
      id: 'coa-register-toggle',
      title: `Bank Account & Credit Card <span class="t3-shepherd-step-count">${stepCounter(7)}</span>`,
      text: 'For Asset accounts, toggle "Bank Account" to make it appear in the Bank Accounts register. For Liability accounts, toggle "Credit Card" to show it in Credit Cards. <strong>Only accounts with this toggle enabled get their own transaction register.</strong>',
      attachTo: { element: '[data-tour="coa-register-toggle"]', on: 'bottom' },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Close Form',
          classes: 't3-shepherd-next-btn',
          action() {
            // Close the group-level inline form by clicking Cancel
            const cancelBtn = document.querySelector('[data-tour="coa-register-toggle"]')
              ?.closest('table')
              ?.querySelector('button');
            // Fallback: click the group add button again or find the Cancel button
            const allButtons = document.querySelectorAll('button');
            for (const btn of allButtons) {
              if (btn.textContent.trim() === 'Cancel' && btn.closest('table')) {
                btn.click();
                break;
              }
            }
            setTimeout(() => this.next(), 400);
          },
        },
      ],
    },
    {
      id: 'coa-complete',
      title: `You're All Set! <span class="t3-shepherd-step-count">${stepCounter(8)}</span>`,
      text: 'That covers the essentials of the Chart of Accounts. Remember: account order here controls report order, sub accounts roll up into parents, and the Bank Account/Credit Card toggle determines which accounts get their own register.',
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Done',
          classes: 't3-shepherd-next-btn',
          action() { this.complete(); },
        },
      ],
    },
  ];
}

registerTour({
  id: 'chartOfAccounts',
  pathPattern: '/foundation/chart-of-accounts',
  getSteps,
  label: 'Chart of Accounts Tour',
});
